<template>
	<div v-loading="loading" class="delivery-progress-card">
		<template v-if="steps.length">
			<div
				v-for="(step, stepIndex) in steps"
				:key="stepIndex"
				:class="['delivery-step-item', { done: step.completed, last: stepIndex === steps.length - 1 }]"
			>
				<div class="delivery-step-line"></div>
				<div class="delivery-step-dot"></div>
				<div class="delivery-step-content">
					<div class="delivery-step-title">
						<span>{{ step.title }}</span>
						<span v-if="step.statusText" :class="['delivery-step-status', { reject: step.isReject }]">
							{{ step.statusText }}
						</span>
					</div>
					<div v-if="step.time" class="delivery-step-time">{{ step.time }}</div>
					<div v-if="step.value" class="delivery-step-opinion">
						<span>{{ step.valueLabel }}</span>
						<a v-if="step.url" class="delivery-step-link" :href="step.url" target="_blank" rel="noopener noreferrer">
							{{ step.value }}
						</a>
						<template v-else>{{ step.value }}</template>
					</div>
				</div>
			</div>
		</template>
		<div v-else class="delivery-progress-empty">暂无进度信息</div>
	</div>
</template>

<script>
	import { queryMerchantOrderDeliveryProgress } from '@/api/mall/index.js'

	/**
	 * @description 发货进度卡片组件
	 * @property {String} orderId 订单ID
	 */
	export default {
		name: 'DeliveryProgressCard',
		props: {
			orderId: {
				type: String,
				default: ''
			},
			visible: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				loading: false,
				steps: []
			}
		},
		watch: {
			visible(newVal) {
				if (newVal && this.orderId) {
					this.fetchProgress()
				}
			}
		},
		methods: {
			// 查询发货进度数据
			fetchProgress() {
				const orderId = this.orderId
				if (!orderId) return

				this.loading = true
				this.steps = []

				queryMerchantOrderDeliveryProgress({ id: orderId })
					.then(response => {
						if (this.orderId !== orderId) return
						const { data } = response || {}
						this.steps = this.parseSteps(data)
					})
					.catch(() => {
						if (this.orderId !== orderId) return
						this.steps = []
					})
					.finally(() => {
						if (this.orderId === orderId) {
							this.loading = false
						}
					})
			},

			// 解析进度数据
			parseSteps(data) {
				const list = Array.isArray(data) ? data : []
				return list.map((item, index) => this.formatStep(item, index))
			},

			// 格式化单个进度节点
			formatStep(item, index) {
				const parsed = this.parseTitleAndStatus(item.key || '', item.status || '')
				const sort = this.getStepSort(item, index)

				return {
					title: parsed.title,
					completed: !!item.completed,
					time: item.time || '',
					value: item.value || '',
					url: item.url || '',
					sort,
					statusText: parsed.statusText,
					isReject: parsed.statusText === '不通过',
					valueLabel: this.getValueLabel(sort, parsed.statusText, parsed.title)
				}
			},
			getStepSort(item, index) {
				const sort = item.sort !== undefined ? item.sort : item.soft || item.orderSort || item.sortNo || item.stepSort || item.orderNum
				const sortNum = Number(sort)
				return Number.isNaN(sortNum) ? index + 1 : sortNum
			},
			getValueLabel(sort, statusText, title) {
				if (String(title || '').trim() === '服务商发货') return '详情：'
				if (String(title || '').trim() === '确认收货') return '详细：'
				if (Number(sort) === 3) return '标题：'
				return statusText === '不通过' ? '原因：' : '意见：'
			},
			parseTitleAndStatus(title, statusText) {
				const cleanTitle = String(title || '').trim()
				const cleanStatus = String(statusText || '').trim()
				if (cleanStatus) {
					return {
						title: cleanTitle.replace(new RegExp(`\\s*${cleanStatus}$`), ''),
						statusText: cleanStatus
					}
				}
				const matched = cleanTitle.match(/^(.*?)[\s　]*(通过|不通过)$/)
				if (matched && matched[1]) {
					return {
						title: matched[1].trim(),
						statusText: matched[2]
					}
				}
				return {
					title: cleanTitle,
					statusText: ''
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.delivery-progress-card {
		padding: 15px 20px;
		border-radius: 4px;
		overflow-y: auto;
		background: linear-gradient(180deg, #edf4ff 0%, #f7fbff 100%);
		// background-image: url('~@/assets/member/steps-bg.png');
		// background-repeat: no-repeat;
		// background-size: cover;
		// background-position: center;
	}

	.delivery-step-item {
		position: relative;
		padding-left: 24px;
		padding-bottom: 20px;

		&:last-child,
		&.last {
			padding-bottom: 0;

			.delivery-step-line {
				display: none;
			}
		}
	}

	.delivery-step-line {
		position: absolute;
		left: 6px;
		top: 10px;
		bottom: -10px;
		width: 2px;
		background: #e4e7ed;
	}

	.delivery-step-dot {
		position: absolute;
		left: 0;
		top: 6px;
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background: #c0c4cc;
		border: 2px solid #fff;
		box-shadow: 0 0 0 1px #c0c4cc;
		z-index: 1;
	}

	.delivery-step-item.done {
		.delivery-step-dot {
			background: #409eff;
			box-shadow: 0 0 0 1px #409eff;
		}

		.delivery-step-line {
			background: #409eff;
		}
	}

	.delivery-step-content {
		margin-top: -2px;
	}

	.delivery-step-title {
		font-size: 14px;
		color: #303133;
		font-weight: 500;
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.delivery-step-status {
		color: #67c23a;
		background: #f0f9eb;
		padding: 2px 8px;
		border-radius: 4px;
		font-size: 12px;

		&.reject {
			color: #f56c6c;
			background: #fef0f0;
		}
	}

	.delivery-step-time {
		font-size: 13px;
		color: #909399;
		margin-top: 6px;
	}

	.delivery-step-opinion {
		font-size: 13px;
		color: #606266;
		margin-top: 8px;
		line-height: 1.6;
		word-break: break-all;

		span {
			color: #303133;
			font-weight: 500;
		}

		.delivery-step-link {
			color: #2671cb;
			text-decoration: none;

			&:hover {
				color: #409eff;
				text-decoration: underline;
			}
		}
	}

	.delivery-progress-empty {
		text-align: center;
		color: #909399;
		font-size: 13px;
		padding: 20px 0;
	}
</style>
